import LoadingSpinner from "components/loading-spinner.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  clearContestState,
  fetchBetsForContestEntry,
  getContestsState,
  getCurrentEntryIdByContestId,
  getEntriesByContestId,
  setContestCurrentEntry,
  setNewContestEntryState
} from "state/slices/contests-slice.ts";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import useContestData from "hooks/useContestData.ts";
import { MyPicksPopout } from "components/contests/my-contests";
import ContestModal from "components/contests/contest-modal/contest-modal.tsx";
import { getGroupState, setGroup } from "state/slices/group-slice.ts";
import Header from "components/header";
import LoginButtons from "components/login-buttons";
import Headroom from "react-headroom";
import { useInovioSuccessfulPaymentRedirectHandler } from "hooks/useInovioSuccessfulPaymentRedirectHandler.ts";
import { Contest } from "interfaces/leaderboard/contest.ts";
import ContestPageContestView from "components/contests/contest-details/header";
import { clearContestBanner, setContestBanner, } from "state/slices/adContestSlice.ts";
import { getContestPath, getGroupDetailsPath, getPoolsPath, } from "utils/backend-path-builders.ts";
import { getRequest } from "utils/httpClient.ts";
import { toast } from "react-toastify";
import { Pool } from "interfaces/pool.ts";
import { POOL_STATUS } from "utils/constants.ts";
import ContestPostJoinInfoModal
  from "components/contests/contest-post-join-info-modal/contest-post-join-info-modal.tsx";
import HomepagePoolsList from "components/homepage-pools-list";
import { getIntegratedAppState, IntegratedAppState } from "state/slices/integrated-app-slice";
import { fetchBets } from "state/slices/bets-slice.ts";

const ContestPage = () => {
  const { id: contestId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const groupId = useAppSelector(getGroupState).id;

  const integratedAppState = useAppSelector(getIntegratedAppState);

  const contestsState = useAppSelector(getContestsState);

  const currentContestEntryId = useAppSelector(state => getCurrentEntryIdByContestId(state, contestId));

  const contestEntries =
    useAppSelector((state) => getEntriesByContestId(state, contestId)) || [];

  const sortedContestEntriesOldestFirst =
    contestEntries.length > 0
      ? [...contestEntries].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
      : [];

  const [showJoinedContestBanner, setShowJoinedContestBanner] = useState(false);

  const [pools, setPools] = useState<Pool[]>([]);
  const [loadingPools, setLoadingPools] = useState<boolean>(false);

  const navigate = useNavigate();

  const { loadingUserContests } = useContestData();

  const [groupChecked, setGroupChecked] = useState(false);

  const [loadingContest, setLoadingContest] = useState(false);
  const [contest, setContest] = useState<Contest>();

  const fetchPools = (skipLoading: boolean) => {
    if (!groupId || !contestId) {
      return;
    }
    const path = getPoolsPath(groupId, {
      contestId: contestId,
      pg: 1,
      page_size: 1000,
    });

    const sortPools = (pools: Pool[]) => {
      pools.sort((a, b) => {
        if (a.status !== b.status) {
          return a.status - b.status;
        }

        if (a.weight !== b.weight) {
          return b.weight - a.weight;
        }

        return a.close - b.close;
      });
    };

    // Only use loading state on initial page load
    if (!skipLoading) {
      setLoadingPools(true);
    }
    getRequest(path, { skipIntegrationApi: true })
      .then((response: Pool[]) => {
        const filteredPools = response.filter((pool) =>
          [POOL_STATUS.OPEN, POOL_STATUS.LIVE, POOL_STATUS.SETTLED].includes(
            pool.status
          )
        );

        sortPools(filteredPools);

        setPools(filteredPools);
      })
      .catch(() => toast.error("Something went wrong."))
      .finally(() => setLoadingPools(false));
  };

  // fetch pools
  useEffect(() => {
    if (contestId) {
      fetchPools(false);
    }
  }, [groupId, contestId]);

  useEffect(() => {
    if (!currentContestEntryId || integratedAppState.state === IntegratedAppState.UNDEFINED) {
      return;
    }

    dispatch(fetchBetsForContestEntry(currentContestEntryId));
  }, [currentContestEntryId, integratedAppState.state, dispatch]);

  // fetch contest
  useEffect(() => {
    if (!contestId) {
      return;
    }

    setLoadingContest(true);
    getRequest(getContestPath(groupId, contestId), { skipIntegrationApi: true })
      .then((contestById) => {
        setContest(contestById);

        if (contestById.group_id && contestById.group_id !== groupId) {
          const getGroupPath = getGroupDetailsPath(contestById.group_id);

          getRequest(getGroupPath, { skipIntegrationApi: true })
            .then((data) => {
              dispatch(setGroup(data));
              dispatch(clearContestState());
            })
            .finally(() => setGroupChecked(true));
        } else {
          setGroupChecked(true);
        }
      })
      .catch(() => toast.error("Something went wrong."))
      .finally(() => setLoadingContest(false));
  }, [contestId, contestsState.contestsStale]);

  // joined banner state
  useEffect(() => {
    const newContestEntry = contestsState.newContestEntry;
    if (newContestEntry) {
      setShowJoinedContestBanner(!newContestEntry.bannerHasBeenShown);
    } else {
      setShowJoinedContestBanner(false);
    }
  }, [contestsState.newContestEntry]);

  useEffect(() => {
    if (contestId && sortedContestEntriesOldestFirst.length) {
      const latestEntry =
        sortedContestEntriesOldestFirst[
        sortedContestEntriesOldestFirst.length - 1
          ];
      dispatch(setContestCurrentEntry({ contestId: contestId, entryId: latestEntry.id }));
    }
  }, [sortedContestEntriesOldestFirst.length, contestId, loadingUserContests]);

  const isWrongGroup = contest && contest.group_id !== groupId;

  // joined banner state
  useEffect(() => {
    if (contest && contest.settings?.ad_banner?.enabled) {
      dispatch(setContestBanner(contest.settings.ad_banner));
    }

    // Cleanup function to clear the contest banner when unmounting
    return () => {
      dispatch(clearContestBanner());
    };
  }, [contest, dispatch]);

  // fetching bets for contest entry
  useEffect(() => {
    if (!groupId || !currentContestEntryId || integratedAppState.state === IntegratedAppState.UNDEFINED) {
      return;
    }

    dispatch(
      fetchBets({
        groupId,
        contestEntryId: currentContestEntryId!!,
        isIntegratedApp: integratedAppState.state !== IntegratedAppState.NOT_INTEGRATED,
      })
    );
  }, [groupId, currentContestEntryId, integratedAppState.state, dispatch]);

  useInovioSuccessfulPaymentRedirectHandler({
    whenRedirectedFromPaymentPage: () => {
      setShowDetailsModal(true);
    },
  });

  if (
    ((loadingContest || loadingUserContests) && !contest) ||
    isWrongGroup ||
    !groupChecked
  ) {
    return <LoadingSpinner />;
  } else if (!contest) {
    navigate("/");
    return;
  }

  return (
    <>
      <Headroom>
        <Header />
        <LoginButtons />
      </Headroom>

      {contest && <ContestPageContestView
        contest={contest}
        userContestEntries={sortedContestEntriesOldestFirst}
        setShowModal={setShowDetailsModal}
      />}

      {loadingPools && <LoadingSpinner />}

      {!loadingPools && (
        <HomepagePoolsList
          refetchPoolData={() => fetchPools(true)}
          pools={pools}
          isContestPool={true}
          contestEntryId={currentContestEntryId}
        />
      )}

      {currentContestEntryId && (<MyPicksPopout contestEntryId={currentContestEntryId} />)}

      <ContestModal
        contest={contest}
        show={showDetailsModal}
        setShow={setShowDetailsModal}
      />

      <ContestPostJoinInfoModal
        show={showJoinedContestBanner}
        entryFee={contest.entry_fee}
        onHide={() => {
          const newContestEntry = contestsState.newContestEntry;
          if (newContestEntry) {
            dispatch(setNewContestEntryState({
              id: newContestEntry.id,
              balanceHasBeenAnimated: newContestEntry.balanceHasBeenAnimated,
              bannerHasBeenShown: true
            }));
          }
        }}
        initialBalance={1000} // hardcoded for all the contests for now
      />
    </>
  );
};

export default ContestPage;
